import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChatModalProps, Message, MessageGroup } from './ChatModal.types';
import './ChatModal.styles.css';

// Helper function to format time
const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });
};

// Helper function to format date for separators
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Aujourd'hui";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Hier';
  } else {
    return date.toLocaleDateString('fr-BE', {
      day: 'numeric',
      month: 'long',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }
};

// Helper function to get user initials
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Helper function to group messages
const groupMessages = (messages: Message[]): MessageGroup[] => {
  const groups: MessageGroup[] = [];
  let currentGroup: MessageGroup | null = null;

  messages.forEach((message) => {
    const messageTime = new Date(message.created_at).getTime();
    
    if (!currentGroup || 
        currentGroup.author.id !== message.author.id ||
        messageTime - new Date(currentGroup.startTime).getTime() > 3 * 60 * 1000) {
      // Start new group if author changed or more than 3 minutes passed
      currentGroup = {
        author: message.author,
        messages: [message],
        startTime: message.created_at
      };
      groups.push(currentGroup);
    } else {
      currentGroup.messages.push(message);
    }
  });

  return groups;
};

export const ChatModal: React.FC<ChatModalProps> = ({
  isOpen,
  onClose,
  task,
  currentUserId,
  onSendMessage,
  onRefresh,
  messages = [],
  loading = false
}) => {
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const threadRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Create initial task message from creator
  const allMessages = useMemo(() => {
    if (!task) return messages;
    
    // Create the initial task message
    const taskMessage: Message = {
      id: `task-${task.id}`,
      text: task.message,
      author: {
        id: task.created_by || 0,
        name: task.created_by_name || 'Système',
        initials: task.created_by_name ? getInitials(task.created_by_name) : 'S'
      },
      created_at: task.created_at,
      // Task creation message is only "own" if current user created it
      is_own: task.created_by ? String(task.created_by) === String(currentUserId) : false
    };
    
    return [taskMessage, ...messages];
  }, [task, messages, currentUserId]);
  
  // Group messages by author and time
  const messageGroups = useMemo(() => groupMessages(allMessages), [allMessages]);

  // Get current date sections
  const dateSections = useMemo(() => {
    const sections = new Map<string, MessageGroup[]>();
    
    messageGroups.forEach(group => {
      const date = new Date(group.startTime).toDateString();
      if (!sections.has(date)) {
        sections.set(date, []);
      }
      sections.get(date)!.push(group);
    });

    return sections;
  }, [messageGroups]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '44px';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = Math.min(scrollHeight, 160) + 'px';
    }
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (threadRef.current && autoScroll) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [autoScroll]);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (threadRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = threadRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setAutoScroll(isAtBottom);
    }
  }, []);

  // Handle send message
  const handleSend = async () => {
    if (!messageText.trim() || sending) return;

    const text = messageText.trim();
    setMessageText('');
    setSending(true);

    try {
      await onSendMessage?.(text);
      // Keep focus on textarea and reset height after sending
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.style.height = '44px';
        }
      }, 0);
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessageText(text); // Restore message on error
    } finally {
      setSending(false);
      // Ensure focus returns to textarea even after errors
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement?.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement?.focus();
            e.preventDefault();
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      firstElement?.focus();

      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    scrollToBottom();
  }, [allMessages, scrollToBottom]);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Auto-resize textarea on input
  useEffect(() => {
    adjustTextareaHeight();
  }, [messageText, adjustTextareaHeight]);

  if (!isOpen || !task) return null;

  return createPortal(
    <div className="cx-chat-modal" role="dialog" aria-modal="true" ref={modalRef}>
      <div className="cx-overlay" onClick={onClose} />
      <div className="cx-card cx-card--wide">
        {/* Header */}
        <header className="cx-chat-header">
          <nav className="cx-breadcrumbs">
            <span>Mes Tâches</span>
            <span className="cx-breadcrumb-separator">›</span>
            <span className="cx-breadcrumb-current">{task.message.slice(0, 30)}...</span>
          </nav>
          
          <div className="cx-header-main">
            <div className="cx-badges">
              {task.deadline && (
                <span className="cx-badge cx-badge--date">
                  <svg className="cx-badge-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  {new Date(task.deadline).toLocaleDateString('fr-BE')}
                </span>
              )}

              {task.created_by_name && (
                <span className="cx-badge cx-badge--author">
                  <svg className="cx-badge-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Par {task.created_by_name}
                </span>
              )}
              
              <span className="cx-badge cx-badge--created">
                <svg className="cx-badge-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                {new Date(task.created_at).toLocaleDateString('fr-BE')}
              </span>
            </div>

            {task.participants && task.participants.length > 0 && (
              <div className="cx-participants">
                <span className="cx-participants-label">PARTICIPANTS</span>
                <div className="cx-participants-list">
                  {task.participants.map((user) => (
                    <div key={user.id} className="cx-participant-item">
                      <div className="cx-participant-avatar">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} />
                        ) : (
                          <span className="cx-avatar-initials">{user.initials || getInitials(user.name)}</span>
                        )}
                      </div>
                      <span className="cx-participant-name">
                        {user.role ? user.role : user.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="cx-header-actions">
            <button 
              className="cx-icon-btn" 
              aria-label="Rafraîchir"
              onClick={onRefresh}
              title="Rafraîchir"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
            <button 
              className="cx-icon-btn" 
              aria-label="Fermer"
              onClick={onClose}
              title="Fermer (Esc)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </header>

        {/* Thread Body */}
        <section 
          className="cx-thread" 
          role="list"
          ref={threadRef}
          onScroll={handleScroll}
        >
          {loading && messages.length === 0 ? (
            <div className="cx-loading">
              <div className="cx-loading-spinner" />
              <span>Chargement des messages...</span>
            </div>
          ) : allMessages.length === 0 ? (
            <div className="cx-empty">
              <p>Aucun message pour le moment.</p>
              <p>Commencez la conversation ci-dessous.</p>
            </div>
          ) : (
            <>
              {Array.from(dateSections.entries()).map(([dateString, groups]) => (
                <React.Fragment key={dateString}>
                  <div className="cx-day-separator">
                    <span>{formatDate(groups[0].startTime)}</span>
                  </div>
                  
                  {groups.map((group, groupIndex) => (
                    <div key={`${dateString}-${groupIndex}`} className="cx-message-group">
                      {group.messages.map((message, messageIndex) => {
                        // Check if this is the task creation message
                        const isTaskMessage = message.id.startsWith('task-');
                        
                        // Use is_own if provided, otherwise compare IDs with type coercion
                        const isOwn = message.is_own !== undefined 
                          ? message.is_own 
                          : String(message.author.id) === String(currentUserId);
                        const showAvatar = messageIndex === 0 && !isOwn && !isTaskMessage;
                        
                        return (
                          <div
                            key={message.id}
                            className={`cx-message-row ${isTaskMessage ? 'cx-message--task' : isOwn ? 'cx-message--me' : 'cx-message--other'}`}
                            role="listitem"
                          >
                            {showAvatar && (
                              <div className="cx-message-avatar">
                                {group.author.avatar ? (
                                  <img src={group.author.avatar} alt={group.author.name} />
                                ) : (
                                  <span className="cx-avatar-initials">
                                    {group.author.initials || getInitials(group.author.name)}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            <div className="cx-message-content">
                              <div className={`cx-bubble ${isTaskMessage ? 'cx-bubble--task' : ''} ${message.status === 'error' ? 'cx-bubble--error' : ''}`}>
                                {message.text}
                                {message.attachments && message.attachments.length > 0 && (
                                  <div className="cx-attachments">
                                    {message.attachments.map(attachment => (
                                      <span key={attachment.id} className="cx-attachment-chip">
                                        📎 {attachment.name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {(messageIndex === group.messages.length - 1 || isTaskMessage) && (
                                <div className={isTaskMessage ? 'cx-task-meta' : ''}>
                                  {isTaskMessage && (
                                    <>
                                      <span className="cx-task-author">{message.author.name}</span>
                                      <span className="cx-task-separator">•</span>
                                    </>
                                  )}
                                  <time 
                                    className="cx-timestamp" 
                                    dateTime={message.created_at}
                                    title={new Date(message.created_at).toLocaleString('fr-BE')}
                                  >
                                    {formatTime(message.created_at)}
                                  </time>
                                </div>
                              )}
                              {message.status === 'error' && (
                                <button className="cx-retry-btn" aria-label="Réessayer">
                                  ↻ Réessayer
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </>
          )}

          {!autoScroll && messages.length > 0 && (
            <button 
              className="cx-jump-to-bottom"
              onClick={() => {
                setAutoScroll(true);
                scrollToBottom();
              }}
              aria-label="Aller au dernier message"
            >
              ↓ Nouveaux messages
            </button>
          )}
        </section>

        {/* Composer Footer */}
        <footer className="cx-composer">
          <div className="cx-composer-helper">
            Entrée pour envoyer • Shift+Entrée pour nouvelle ligne
          </div>
          <div className="cx-composer-row">
            <textarea
              ref={textareaRef}
              className="cx-composer-textarea"
              placeholder="Écrire un commentaire..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              aria-label="Message"
            />
            <div className="cx-composer-actions">
              <button 
                className="cx-attach-btn"
                aria-label="Joindre un fichier"
                disabled
                title="Bientôt disponible"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              </button>
              <button
                className="cx-send-btn"
                onClick={handleSend}
                disabled={!messageText.trim() || sending}
                aria-label="Envoyer"
              >
                {sending ? (
                  <div className="cx-send-spinner" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default ChatModal;